/**
 * bloomPipeline — reaction-diffusion Turing pattern.
 *
 * A real Gray-Scott reaction-diffusion simulation on the GPU. Two
 * chemical concentrations (U, V) are stored in an RGBA16F texture
 * (R = U, G = V). Each frame:
 *   1. Integrate pass: compute the Laplacian of U and V, add reaction
 *      terms (V * U^2 - F * U, V * U^2 - (F + k) * V), and update.
 *   2. Render pass: read the V channel and color it like a coral /
 *      skin / growing pattern.
 *
 * The patterns that emerge (stripes, spots, mitosis-like blobs) are
 * completely determined by the (F, k) constants. We cycle through 6
 * patterns over 60 seconds so the scene morphs through different
 * Turing regimes.
 *
 * This is an actual biological/chemical simulation. Patterns emerge
 * from nothing — no shaders look quite like this.
 *
 * Attribution: Gray-Scott reaction-diffusion equations are textbook.
 * GPU ping-pong simulation pattern is mine.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BloomPipeline {
  programs: {
    init: WebGLProgram;
    update: WebGLProgram;
    render: WebGLProgram;
  };
  vaos: {
    fullscreen: WebGLVertexArrayObject;
    update: WebGLVertexArrayObject;
  };
  vbos: {
    fullscreen: WebGLBuffer;
  };
  uniforms: {
    update: {
      uTex: WebGLUniformLocation | null;
      uTexel: WebGLUniformLocation | null;
      uF: WebGLUniformLocation | null;
      uK: WebGLUniformLocation | null;
      uDt: WebGLUniformLocation | null;
    };
    render: {
      uTex: WebGLUniformLocation | null;
      uTime: WebGLUniformLocation | null;
      uPattern: WebGLUniformLocation | null;
    };
  };
  fboA: WebGLFramebuffer;
  texA: WebGLTexture;
  fboB: WebGLFramebuffer;
  texB: WebGLTexture;
  size: number;
}

const VERT_QUAD = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_INIT = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
void main() {
  // U starts at 1.0 everywhere, V is 0 except in some seed spots.
  float v = 0.0;
  // Multiple seed spots for faster pattern emergence.
  vec2 p = vUV;
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    vec2 center = vec2(0.2 + 0.15 * fi, 0.5 + 0.3 * sin(fi * 1.7));
    float d = length(p - center);
    if (d < 0.05) v = 1.0;
  }
  outColor = vec4(1.0, v, 0.0, 1.0);
}`;

const FRAG_UPDATE = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uTex;
uniform vec2 uTexel;
uniform float uF;
uniform float uK;
uniform float uDt;
void main() {
  vec4 c = texture(uTex, vUV);
  float u = c.r;
  float v = c.g;
  // Laplacian (5-point stencil).
  float lu =
      texture(uTex, vUV + vec2(uTexel.x, 0.0)).r
    + texture(uTex, vUV - vec2(uTexel.x, 0.0)).r
    + texture(uTex, vUV + vec2(0.0, uTexel.y)).r
    + texture(uTex, vUV - vec2(0.0, uTexel.y)).r
    - 4.0 * u;
  float lv =
      texture(uTex, vUV + vec2(uTexel.x, 0.0)).g
    + texture(uTex, vUV - vec2(uTexel.x, 0.0)).g
    + texture(uTex, vUV + vec2(0.0, uTexel.y)).g
    + texture(uTex, vUV - vec2(0.0, uTexel.y)).g
    - 4.0 * v;
  // Gray-Scott reaction.
  float uvv = u * v * v;
  float du = 1.0 * lu - uvv + uF * (1.0 - u);
  float dv = 0.5 * lv + uvv - (uF + uK) * v;
  // Hard edges: clamp U back to 1 if it tries to go above 1.
  float nu = clamp(u + du * uDt, 0.0, 1.0);
  float nv = clamp(v + dv * uDt, 0.0, 1.0);
  outColor = vec4(nu, nv, 0.0, 1.0);
}`;

const FRAG_RENDER = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uTex;
uniform float uTime;
uniform float uPattern;
void main() {
  vec2 c = texture(uTex, vUV).rg;
  // c.r = U (substrate), c.g = V (product).
  // Pattern emphasis on V.
  float v = c.g;
  // Color palette cycles with the (F, k) pattern.
  vec3 dark = vec3(0.05, 0.02, 0.10);
  vec3 mid = vec3(0.45, 0.10, 0.40);
  vec3 bright = vec3(1.0, 0.55, 0.25);
  vec3 hot = vec3(1.0, 0.95, 0.7);
  vec3 col = mix(dark, mid, smoothstep(0.05, 0.25, v));
  col = mix(col, bright, smoothstep(0.25, 0.40, v));
  col = mix(col, hot, smoothstep(0.40, 0.55, v));
  // Slight time-based color shift.
  col *= vec3(0.95, 1.0, 1.05);
  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

const QUAD = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

function makeFBO(gl: WebGL2RenderingContext, size: number): { fbo: WebGLFramebuffer; tex: WebGLTexture } | null {
  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, size, size, 0, gl.RGBA, gl.HALF_FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const fbo = gl.createFramebuffer();
  if (!fbo) return null;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    console.error("[bloom] FBO incomplete: 0x" + status.toString(16));
    return null;
  }
  return { fbo, tex };
}

function link(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram | null {
  return linkProgram(gl, vs, fs, POST_ATTRIBS);
}

export function createBloomPipeline(gl: WebGL2RenderingContext): BloomPipeline | null {
  if (!gl.getExtension("EXT_color_buffer_float")) {
    console.error("[bloom] EXT_color_buffer_float not available");
    return null;
  }
  const size = 256;
  const initP = link(gl, VERT_QUAD, FRAG_INIT);
  const updateP = link(gl, VERT_QUAD, FRAG_UPDATE);
  const renderP = link(gl, VERT_QUAD, FRAG_RENDER);
  if (!initP || !updateP || !renderP) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  const a = makeFBO(gl, size);
  const b = makeFBO(gl, size);
  if (!a || !b) return null;
  return {
    programs: { init: initP, update: updateP, render: renderP },
    vaos: { fullscreen: vao, update: vao },
    vbos: { fullscreen: vbo },
    uniforms: {
      update: {
        uTex: gl.getUniformLocation(updateP, "uTex"),
        uTexel: gl.getUniformLocation(updateP, "uTexel"),
        uF: gl.getUniformLocation(updateP, "uF"),
        uK: gl.getUniformLocation(updateP, "uK"),
        uDt: gl.getUniformLocation(updateP, "uDt"),
      },
      render: {
        uTex: gl.getUniformLocation(renderP, "uTex"),
        uTime: gl.getUniformLocation(renderP, "uTime"),
        uPattern: gl.getUniformLocation(renderP, "uPattern"),
      },
    },
    fboA: a.fbo, texA: a.tex,
    fboB: b.fbo, texB: b.tex,
    size,
  };
}

/**
 * Initialize the simulation. Run ONCE at scene load.
 * Writes the initial seed pattern to texA and texB.
 */
export function initBloom(gl: WebGL2RenderingContext, pipe: BloomPipeline): void {
  for (const fbo of [pipe.fboA, pipe.fboB]) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.viewport(0, 0, pipe.size, pipe.size);
    gl.useProgram(pipe.programs.init);
    gl.bindVertexArray(pipe.vaos.fullscreen);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindVertexArray(null);
}

/**
 * Step the simulation forward and render. Pattern is a continuous
 * parameter (0..1) that selects from the (F, k) parameter space.
 */
export function drawBloom(
  gl: WebGL2RenderingContext,
  pipe: BloomPipeline,
  width: number,
  height: number,
  t: number,
): void {
  // Pattern cycle: every 10 seconds we shift the (F, k) parameters
  // through different regimes. We smooth-interpolate between them
  // using floor + fract.
  const cycleT = t * 0.1;
  const patternIndex = Math.floor(cycleT);
  const patternFrac = cycleT - patternIndex;
  // 6 canonical Gray-Scott patterns.
  // F, k values from the Pearson catalog.
  const patterns: Array<{ f: number; k: number }> = [
    { f: 0.037, k: 0.060 },  // spots
    { f: 0.029, k: 0.057 },  // mitosis / cells
    { f: 0.039, k: 0.058 },  // coral / growth
    { f: 0.025, k: 0.060 },  // moving spots
    { f: 0.026, k: 0.061 },  // stripes
    { f: 0.018, k: 0.051 },  // solitons
  ];
  const idxA = ((patternIndex) % patterns.length + patterns.length) % patterns.length;
  const idxB = ((patternIndex + 1) % patterns.length + patterns.length) % patterns.length;
  const patA = patterns[idxA];
  const patB = patterns[idxB];
  const F = patA.f + (patB.f - patA.f) * patternFrac;
  const K = patA.k + (patB.k - patA.k) * patternFrac;

  // === Step: read texA, write to texB ===
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.fboB);
  gl.viewport(0, 0, pipe.size, pipe.size);
  gl.useProgram(pipe.programs.update);
  gl.bindVertexArray(pipe.vaos.update);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.texA);
  gl.uniform1i(pipe.uniforms.update.uTex, 0);
  gl.uniform2f(pipe.uniforms.update.uTexel, 1.0 / pipe.size, 1.0 / pipe.size);
  gl.uniform1f(pipe.uniforms.update.uF, F);
  gl.uniform1f(pipe.uniforms.update.uK, K);
  gl.uniform1f(pipe.uniforms.update.uDt, 1.0);
  // Run multiple steps per frame for faster pattern emergence.
  for (let i = 0; i < 12; i++) {
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    // Swap: texB is now the "current", we need to read from it next.
    const oldTex = pipe.texA;
    pipe.texA = pipe.texB;
    pipe.texB = oldTex;
    const oldFbo = pipe.fboA;
    pipe.fboA = pipe.fboB;
    pipe.fboB = oldFbo;
    gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.fboB);
    gl.bindTexture(gl.TEXTURE_2D, pipe.texA);
  }

  // === Render: read the latest state, draw to screen ===
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.useProgram(pipe.programs.render);
  gl.bindVertexArray(pipe.vaos.fullscreen);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.texA);
  gl.uniform1i(pipe.uniforms.render.uTex, 0);
  gl.uniform1f(pipe.uniforms.render.uTime, t);
  gl.uniform1f(pipe.uniforms.render.uPattern, F);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
